<template>
  <KikModal
    id="reset-password-modal"
    :is-open="isResetPasswordModalOpen"
    @close="closeModal()"
  >
    <template #content>
      <div class="max-w-[90%] desktop:w-[410px] desktop:max-w-[410px]">
        <h3 class="border-b pb-2">
          {{ $t('Reset Password') }}
        </h3>
        <template v-if="!isPasswordChanged">
          <section
            v-if="errorLogin"
            class="mt-4 rounded border border-primary-400 bg-primary-100 p-3 text-sm text-primary-700"
            v-html="$t(errorLogin)"
          />
          <h4 class="mt-4">
            {{ $t("Create a new password") }}
          </h4>
          <p class="font-light">
            {{ $t("Now you can enter the new password and confirm it.") }}
            <KikTooltip
              :label="$t('passwordNotValid')"
              placement="top"
              show-arrow
            >
              <SfIconInfo
                class="cursor-pointer hover:fill-primary"
                size="sm"
              />
            </KikTooltip>
          </p>
          <form
            class="mt-7 flex flex-col gap-4"
            @submit="onSubmit"
          >
            <div class="mb-3">
              <div class="relative">
                <SfInput
                  v-model="password"
                  name="password"
                  :placeholder="$t('Password')"
                  :type="passwordVisible ? 'text' : 'password'"
                  v-bind="passwordAttrs"
                  :invalid="!!errors.password"
                />
                <div class="absolute bottom-2 right-2 cursor-pointer">
                  <Component
                    :is="passwordVisible ? SfIconVisibility : SfIconVisibilityOff"
                    class="text-neutral-800"
                    @click="passwordVisible = !passwordVisible"
                  />
                </div>
              </div>
              <KikFormError
                class="mt-1"
                :error="errors.password"
              />
            </div>
            <div>
              <div class="relative">
                <SfInput
                  v-model="repeatPassword"
                  name="password"
                  :placeholder="$t('Repeat Password')"
                  :type="repeatPasswordVisible ? 'text' : 'password'"
                  v-bind="repeatPasswordAttrs"
                  :invalid="!!errors.repeatPassword"
                />
                <div class="absolute bottom-2 right-2 cursor-pointer">
                  <Component
                    :is="repeatPasswordVisible ? SfIconVisibility : SfIconVisibilityOff"
                    class="text-neutral-800"
                    @click="repeatPasswordVisible = !repeatPasswordVisible"
                  />
                </div>
              </div>
              <KikFormError
                class="mt-1"
                :error="errors.repeatPassword"
              />
            </div>
            <SfButton
              class="mt-5 w-full"
              :disabled="isSubmitting || (!meta.valid && meta.dirty)"
              v-bind="{ type: 'submit' }"
            >
              <SfLoaderCircular
                v-if="isSubmitting"
                size="base"
              />
              <span v-else>{{ $t('Submit') }}</span>
            </SfButton>
          </form>
        </template>
        <template v-else>
          <p class="mb-10 mt-5 font-light">
            {{ $t("Password Changed") }}
          </p>
        </template>
        <SfLink
          class="mt-6 block cursor-pointer text-xs underline"
          variant="primary"
          @click="handleBackToLogin()"
        >
          {{ $t("login in to your account") }}
        </SfLink>
      </div>
    </template>
  </KikModal>
</template>

<script lang="ts" setup>
import {
  SfButton, SfIconInfo, SfIconVisibility, SfIconVisibilityOff,
  SfInput, SfLink, SfLoaderCircular,
} from '@storefront-ui/vue';
import * as zod from 'zod';
import useUiState from '~/composables/useUiState';
import { useBFF } from '~/composables/useBFF/useBFF';

const route = useRoute();
const { isResetPasswordModalOpen, toggleResetPasswordModal, toggleLoginDrawer } = useUiState();

const { $me } = useBFF();

const passwordVisible = ref(false);
const repeatPasswordVisible = ref(false);
const errorLogin = ref();
const isPasswordChanged = ref(false);

const validationSchema = toTypedSchema(
  zod.object({
    password: zod.string({ error: 'This field is required.' }).min(8, { message: 'Password must have at least 8 characters' }),
    repeatPassword: zod.string({ error: 'This field is required.' }).min(8, { message: 'Password must have at least 8 characters' }),
  }).refine(data => data.password === data.repeatPassword, {
    message: 'Passwords do not match',
    path: ['repeatPassword'],
  }),
);

const { defineField, handleSubmit, errors, isSubmitting, meta } = useForm({
  validationSchema,
});

const [password, passwordAttrs] = defineField('password');
const [repeatPassword, repeatPasswordAttrs] = defineField('repeatPassword');

const closeModal = () => {
  navigateTo({ path: route.path }, { replace: true });
  toggleResetPasswordModal();
};

const handleBackToLogin = () => {
  navigateTo({ path: route.path }, { replace: true });
  toggleResetPasswordModal();
  toggleLoginDrawer();
};

onMounted(() => {
  const { token } = route.query;

  if (token && !isResetPasswordModalOpen.value && route.query.resetPassword === 'true') {
    toggleResetPasswordModal();
  }
});

const onSubmit = handleSubmit(async (values) => {
  errorLogin.value = undefined;
  const tokenValue = typeof route.query?.token === 'string' ? route.query.token.replace(/\?$/, '') : '';

  try {
    const { data }: any = await $me.resetPassword({ newPassword: values.password, token: tokenValue });

    if (data?.id) {
      isPasswordChanged.value = true;
    }
  }
  catch {
    errorLogin.value = 'Password could not be changed. Please try again later.';
    isPasswordChanged.value = false;
  }
});
</script>
